import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button,
  FormGroup,
  Label,
  Input,
  Table,
  Form,
  ListGroup,
  ListGroupItem,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import NotificationAlert from "react-notification-alert";
import "react-notification-alert/dist/animate.css";
import { CursoService } from "service/CursoService";
import { ContenidoService } from "service/ContenidoService";
import ModalTema from "components/Modals/ModalTema";
import ModalSubTema from "components/Modals/ModalSubTema";
import FSelect from "components/Select/FSelect";

function CursoDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();

  const notificationAlertRef = useRef(null);
  const notify = (type, message) => {
    notificationAlertRef.current.notificationAlert({
      place: "tc",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    });
  };

  const [curso, setCurso] = useState({
    titulo: "",
    modulo: "",
    descripcion: "",
    urlimagen: "",
    estado: "Borrador"
  });
  const [temas, setTemas] = useState([]);
  const [listModulos, setListModulos] = useState([]);
  const [listCiclos, setListCiclos] = useState([]);
  const [selectedCiclo, setSelectedCiclo] = useState(localStorage.getItem("_c") || "");
  const [isEditingData, setIsEditingData] = useState(false);

  // Loading state
  const [loadingSave, setLoadingSave] = useState(false);

  // Confirm modal states
  const [confirmModal, setConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState({ mensaje: "", onConfirm: null });

  // Modal states
  const [modalTemaOpen, setModalTemaOpen] = useState(false);
  const [selectedTema, setSelectedTema] = useState(null);

  const [modalSubTemaOpen, setModalSubTemaOpen] = useState(false);
  const [selectedSubTema, setSelectedSubTema] = useState(null);
  const [targetTemaId, setTargetTemaId] = useState(null);

  const toggleModalTema = () => {
    setModalTemaOpen(!modalTemaOpen);
    if (modalTemaOpen) setSelectedTema(null);
  };

  const toggleModalSubTema = () => {
    setModalSubTemaOpen(!modalSubTemaOpen);
    if (!modalSubTemaOpen) { // Cuando se cierra
      setSelectedSubTema(null);
      setTargetTemaId(null);
    }
  };

  const handleEditTema = (tema) => {
    setSelectedTema(tema);
    setModalTemaOpen(true);
  };

  const handleAddSubTema = (temaId) => {
    setTargetTemaId(temaId);
    setModalSubTemaOpen(true);
  };

  const handleEditSubTema = (temaId, subtema) => {
    setTargetTemaId(temaId);
    setSelectedSubTema(subtema);
    setModalSubTemaOpen(true);
  };

  const cursoService = new CursoService();
  const contenidoService = new ContenidoService();

  useEffect(() => {
    loadInitialData();
    if (id !== "new") {
      getCursoData();
    } else {
      setIsEditingData(true);
    }
  }, [id]);

  useEffect(() => {
    if (id !== "new" && selectedCiclo) {
      getTemas();
    }
  }, [selectedCiclo, id]);

  const loadInitialData = async () => {
    try {
      const resModulos = await contenidoService.getModulos();
      const resCiclos = await contenidoService.getCiclos();
      setListModulos(resModulos?.data || []);
      const ciclos = resCiclos?.data || [];
      setListCiclos(ciclos);
      if (!selectedCiclo && ciclos.length > 0) {
        setSelectedCiclo(ciclos[0]._id);
      }
    } catch (error) {
      console.error("Error inicializando datos:", error);
    }
  };

  const getCursoData = async () => {
    try {
      const response = await cursoService.getCursos(); // Ajustar si hay getById
      const found = response?.data.find(c => c._id === id);
      if (found) {
        setCurso(found);
      }
    } catch (error) {
      console.error("Error cargando curso:", error);
    }
  };

  const getTemas = async () => {
    try {
      const response = await contenidoService.getTemas({
        curso: id,
        ciclo: selectedCiclo
      });
      setTemas(response?.data || []);
    } catch (error) {
      console.error("Error cargando temas:", error);
    }
  };

  const handleCursoChange = (e) => {
    const { name, value } = e.target;
    setCurso(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveCurso = async () => {
    setLoadingSave(true);
    try {
      let res;
      if (id === "new") {
        res = await cursoService.addCurso(curso);
      } else {
        res = await cursoService.updateCurso(curso);
      }
      if (res?.confirmation === "success") {
        notify("success", "Curso guardado exitosamente");
        setIsEditingData(false);
        if (id === "new") navigate("/admin/cursos");
      }
    } catch (error) {
      console.error("Error guardando curso:", error);
      notify("danger", "Error al guardar el curso");
    } finally {
      setLoadingSave(false);
    }
  };

  const handleDeleteTema = async (temaId) => {
    setConfirmData({
      mensaje: "¿Estás seguro de eliminar este tema? Esta acción no se puede deshacer.",
      onConfirm: async () => {
        setConfirmModal(false);
        try {
          const res = await contenidoService.deleteTema(temaId);
          if (res?.confirmation === "success") {
            getTemas();
          }
        } catch (error) {
          console.error("Error eliminando tema:", error);
          notify("danger", "Error al eliminar el tema");
        }
      },
    });
    setConfirmModal(true);
  };

  return (
    <>
      <NotificationAlert ref={notificationAlertRef} />
      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              {/* Zona 1: Header */}
              <CardHeader>
                <Row className="align-items-center">
                  <Col md="8">
                    <CardTitle tag="h4">Configuración del Curso</CardTitle>
                  </Col>
                  <Col md="4" className="text-right">
                    <Button color="link" className="btn-icon btn-round" onClick={() => navigate("/admin/cursos")}>
                      <i className="tim-icons icon-minimal-left" />
                    </Button>
                    <Button color="link" className="btn-icon btn-round">
                      <i className="tim-icons icon-settings-gear-63" />
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                {/* Zona 3: Barra de acciones del curso */}
                <Row className="mb-4">
                  <Col md="6"></Col>
                  <Col md="6" className="text-right">
                    {!isEditingData && (
                      <Button color="warning" className="btn-fill" onClick={() => setIsEditingData(true)}>
                        <i className="tim-icons icon-pencil mr-2" />
                        Editar Datos
                      </Button>
                    )}
                    {isEditingData && (
                      <>
                        <Button color="success" className="btn-fill" onClick={handleSaveCurso} disabled={loadingSave}>
                          {loadingSave ? <Spinner size="sm" /> : <><i className="tim-icons icon-check-2 mr-2" />{id === "new" ? "Registrar" : "Actualizar"}</>}
                        </Button>
                        {id !== "new" && (
                          <Button color="secondary" className="btn-fill ml-2" onClick={() => setIsEditingData(false)}>
                            Cancelar
                          </Button>
                        )}
                      </>
                    )}
                  </Col>
                </Row>

                <hr className="my-4" />

                <Form>
                  <Row>
                    <Col md="4">
                      <div className="text-center mb-3">
                        {curso.urlimagen ? (
                          <img
                            src={`https://drive.google.com/uc?id=${curso.urlimagen.split('id=')[1]}`}
                            alt="Portada"
                            style={{ maxWidth: '100%', height: 'auto', borderRadius: '10px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}
                          />
                        ) : (
                          <div className="p-5 bg-secondary text-white rounded" style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Sin Imagen de Portada</div>
                        )}
                        {isEditingData && (
                          <FormGroup className="mt-3 text-left">
                            <Label>URL Portada (ID Google Drive)</Label>
                            <Input
                              type="text"
                              name="urlimagen"
                              placeholder="Ej: https://drive.google.com/open?id=XXXXX"
                              value={curso.urlimagen}
                              onChange={handleCursoChange}
                              style={{ height: '40px' }}
                            />
                          </FormGroup>
                        )}
                      </div>
                    </Col>
                    <Col md="8">
                      <Row>
                        <Col md="8">
                          <FormGroup>
                            <Label>Título del Curso</Label>
                            <Input
                              name="titulo"
                              value={curso.titulo}
                              onChange={handleCursoChange}
                              disabled={!isEditingData}
                              style={{ height: '40px' }}
                            />
                          </FormGroup>
                        </Col>
                        <Col md="4">
                          <FormGroup>
                            <Label>Módulo / Categoría</Label>
                            <FSelect
                              name="modulo"
                              value={curso.modulo?._id || curso.modulo}
                              onChange={handleCursoChange}
                              isDisabled={!isEditingData}
                              options={listModulos.map(m => ({ value: m._id, label: `${m.titulo} ${m.denominacion}` }))}
                              placeholder="Seleccione Módulo"
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md="12">
                          <FormGroup>
                            <Label>Descripción Detallada</Label>
                            <Input
                              type="textarea"
                              name="description"
                              value={curso.descripcion}
                              onChange={handleCursoChange}
                              disabled={!isEditingData}
                              rows="4"
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md="4">
                          <FormGroup>
                            <Label>Estado de Publicación</Label>
                            <FSelect
                              name="estado"
                              value={curso.estado}
                              onChange={handleCursoChange}
                              isDisabled={!isEditingData}
                              options={[
                                { value: "Publicado", label: "Publicado" },
                                { value: "Borrador", label: "Borrador" },
                                { value: "Oculto", label: "Oculto" },
                              ]}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>

            {id !== "new" && (
              <Card>
                {/* Zona 1: Header Contenidos */}
                <CardHeader>
                  <Row className="align-items-center">
                    <Col md="8">
                      <CardTitle tag="h4">Gestión de Contenidos Académicos</CardTitle>
                    </Col>
                    <Col md="4" className="text-right">
                      <Button color="link" className="btn-icon btn-round">
                        <i className="tim-icons icon-settings-gear-63" />
                      </Button>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody>
                  {/* Zona 2: Filtros de Contenido */}
                  <Row className="align-items-end mb-4">
                    <Col md="11">
                      <FormGroup className="mb-0">
                        <Label for="selectedCiclo">Ciclo Académico Actual</Label>
                        <FSelect
                          id="selectedCiclo"
                          value={selectedCiclo}
                          onChange={(e) => {
                            setSelectedCiclo(e.target.value);
                            localStorage.setItem("_c", e.target.value);
                          }}
                          options={listCiclos.map(c => ({ value: c._id, label: c.denominacion }))}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="1">
                      <FormGroup className="mb-0">
                        <Button
                          color="info"
                          block
                          onClick={getTemas}
                          style={{ height: "40px", padding: "0" }}
                          title="Refrescar"
                        >
                          <i className="tim-icons icon-refresh-01" />
                        </Button>
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Zona 3: Acciones de Contenido */}
                  <Row className="mb-4">
                    <Col md="6"></Col>
                    <Col md="6" className="text-right">
                      <Button color="warning" className="btn-fill" onClick={toggleModalTema}>
                        <i className="tim-icons icon-simple-add mr-2" />
                        Agregar Nuevo Tema
                      </Button>
                    </Col>
                  </Row>

                  <hr className="my-4" />

                  <ListGroup>

                    {temas.map((tema, idx) => (
                      <ListGroupItem key={tema._id || idx} className="mb-3 border rounded shadow-sm">
                        <div className="d-flex justify-content-between align-items-center bg-light p-2 rounded">
                          <div>
                            <h5 className="mb-0 text-primary">{tema.titulo}</h5>
                          </div>
                          <div>
                            <Button color="info" size="sm" className="btn-icon btn-link" title="Materiales">
                              <i className="tim-icons icon-paper" />
                            </Button>
                            <Button color="success" size="sm" className="btn-icon btn-link" title="Exámenes">
                              <i className="tim-icons icon-notes" />
                            </Button>
                            <Button
                              color="warning"
                              size="sm"
                              className="btn-icon btn-link"
                              title="Editar Tema"
                              onClick={() => handleEditTema(tema)}
                            >
                              <i className="tim-icons icon-pencil" />
                            </Button>
                            {tema.contenidos?.length === 0 && (
                              <Button
                                color="danger"
                                size="sm"
                                className="btn-icon btn-link"
                                title="Eliminar"
                                onClick={() => handleDeleteTema(tema._id)}
                              >
                                <i className="tim-icons icon-trash-simple" />
                              </Button>
                            )}
                          </div>
                        </div>

                        {tema.contenidos && tema.contenidos.length > 0 && (
                          <Table className="mt-2 ml-4" size="sm" borderless>
                            <thead>
                              <tr>
                                <th style={{ width: '70%' }}>Subtema</th>
                                <th>Estado</th>
                                <th className="text-right">Acciones</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tema.contenidos.map((sub, sIdx) => (
                                <tr key={sub._id || sIdx} className="border-bottom">
                                  <td>{sub.titulo}</td>
                                  <td><small>{sub.estado}</small></td>
                                  <td className="text-right">
                                    <Button
                                      color="info"
                                      size="sm"
                                      className="btn-icon btn-link"
                                      onClick={() => handleEditSubTema(tema._id, sub)}
                                    >
                                      <i className="tim-icons icon-pencil" />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                        <div className="text-right mt-2">
                          <Button
                            color="info"
                            size="sm"
                            outline
                            onClick={() => handleAddSubTema(tema._id)}
                          >
                            + Agregar Subtema
                          </Button>
                        </div>
                      </ListGroupItem>
                    ))}
                    {temas.length === 0 && (
                      <p className="text-center p-4">No hay temas registrados para este ciclo.</p>
                    )}
                  </ListGroup>
                </CardBody>
              </Card>
            )}
          </Col>
        </Row>
      </div>

      <ModalTema
        isOpen={modalTemaOpen}
        toggle={toggleModalTema}
        selectedTema={selectedTema}
        cursoId={id}
        onSave={getTemas}
      />

      <ModalSubTema
        isOpen={modalSubTemaOpen}
        toggle={toggleModalSubTema}
        selectedSubTema={selectedSubTema}
        temaId={targetTemaId}
        cursoId={id}
        onSave={getTemas}
      />

      <Modal isOpen={confirmModal} toggle={() => setConfirmModal(false)} size="sm" centered>
        <ModalHeader toggle={() => setConfirmModal(false)}>Confirmar acción</ModalHeader>
        <ModalBody><p>{confirmData.mensaje}</p></ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setConfirmModal(false)}>Cancelar</Button>
          <Button color="danger" onClick={confirmData.onConfirm}>Confirmar</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default CursoDetalle;
